import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { getCreateCheckoutSessionUrl } from "../lib/checkoutConfig";
import { BRAND_LOGO_LOCKUP } from "../lib/data";
import CommunityRewardCartNote from "./cart/CommunityRewardCartNote";
import { DELIVERY_OPTIONS, deliveryShippingGbp } from "../lib/delivery";
import { getProductPreview } from "../lib/productPreviewImages";

const DELIVERY_STORAGE_KEY = "mossmatrix_delivery_option";

/** Short cart-only labels — prices/ids still come from DELIVERY_OPTIONS. */
const DELIVERY_CARD_COPY = {
  standard: "1–3 working days dispatch",
  priority: "Prepared first where possible",
};

function cartPlaceholderLabel(item) {
  const colour = item.base_type === "purple" ? "Purple" : "Gold";
  const pack = item.packaging_type === "pouch" ? "pouch" : "jar";
  return `${colour} ${pack}`;
}

function CartItemThumb({ item }) {
  const preview = getProductPreview(item.base_type || "gold", item.packaging_type || "jar", item.size_ml);
  const [failed, setFailed] = useState(false);
  const isPurple = item.base_type === "purple";

  if (failed || !preview?.src) {
    return (
      <div
        className={`w-16 h-16 shrink-0 rounded-lg border border-forest/15 grid place-items-center text-center px-1 ${
          isPurple ? "bg-[#f3eef8]" : "bg-[#f7f1e3]"
        }`}
        aria-hidden
      >
        <span className="text-[10px] font-medium text-forest leading-tight">{cartPlaceholderLabel(item)}</span>
      </div>
    );
  }

  return (
    <div className="w-16 h-16 shrink-0 rounded-lg overflow-hidden border border-forest/12 bg-[#f4f1ea]">
      <img
        src={preview.src}
        alt=""
        width={64}
        height={64}
        className="w-full h-full object-contain object-center"
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();
  const [checkoutError, setCheckoutError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [deliveryOption, setDeliveryOption] = useState(() => {
    try {
      const v = sessionStorage.getItem(DELIVERY_STORAGE_KEY);
      return v === "priority" ? "priority" : "standard";
    } catch {
      return "standard";
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(DELIVERY_STORAGE_KEY, deliveryOption);
    } catch {
      /* ignore */
    }
  }, [deliveryOption]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  const shipping = deliveryShippingGbp(deliveryOption);
  const total = subtotal + shipping;
  const itemCount = items.reduce((n, item) => n + (item.qty || 0), 0);

  const checkout = async () => {
    if (!items.length) return;
    setCheckoutError(null);
    setCheckoutLoading(true);
    const url = getCreateCheckoutSessionUrl();
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, delivery_option: deliveryOption }),
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }
      if (!res.ok) {
        const hint =
          res.status === 404
            ? "Checkout endpoint was not found. On local dev, run Netlify with functions enabled and set REACT_APP_NETLIFY_FUNCTIONS_BASE (see PRODUCTION_SETUP.md)."
            : null;
        setCheckoutError(
          (data && (data.error || data.detail)) ||
            hint ||
            `Checkout could not start (${res.status}). Check configuration or try again.`,
        );
        setCheckoutLoading(false);
        return;
      }
      if (data && data.url) {
        window.location.assign(data.url);
        return;
      }
      setCheckoutError("Checkout did not return a payment link. Verify Stripe and Netlify function logs.");
    } catch (e) {
      setCheckoutError(
        "Could not reach checkout. If you are developing locally, set REACT_APP_NETLIFY_FUNCTIONS_BASE to your Netlify dev URL (often http://localhost:8888) or deploy to Netlify first.",
      );
    }
    setCheckoutLoading(false);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 bg-forest/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-hidden
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {open && (
          <motion.aside
            key="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 280, damping: 32 }}
            data-testid="mini-cart-drawer"
            className="fixed top-0 right-0 z-50 h-full w-full sm:w-[460px] bg-offwhite shadow-hover flex flex-col font-sans"
          >
            <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-cream">
              <div className="flex items-center gap-3 min-w-0">
                <span className="shrink-0 inline-flex rounded-full overflow-hidden bg-white ring-1 ring-cream p-0.5">
                  <img src={BRAND_LOGO_LOCKUP} alt="" className="h-9 w-9 object-contain" aria-hidden />
                </span>
                <div className="min-w-0">
                  <h2 className="font-sans font-semibold text-lg sm:text-xl text-forest m-0 leading-tight">Your Basket</h2>
                  <p className="text-xs text-botanical/70 m-0 mt-0.5">
                    {itemCount} {itemCount === 1 ? "item" : "items"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="close-cart-btn"
                className="w-11 h-11 rounded-full border border-forest/15 grid place-items-center hover:bg-forest hover:text-cream transition shrink-0"
                aria-label="Close cart"
              >
                <X className="w-4 h-4" strokeWidth={1.6} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
                <div className="w-14 h-14 rounded-full bg-cream grid place-items-center text-forest">
                  <ShoppingBag className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <p className="font-sans font-semibold text-xl text-forest mt-5 m-0">Your basket is empty</p>
                <p className="text-sm text-botanical/75 mt-2 max-w-xs leading-relaxed m-0">
                  Choose Gold or Purple Sea Moss Gel to get started.
                </p>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-forest text-cream px-6 py-3 text-sm font-medium"
                >
                  Continue shopping
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 space-y-3">
                  {items.map((item) => {
                    const unit = item.unit_price ?? item.price;
                    const lineTotal = item.price * item.qty;
                    const sizeLabel = item.size_ml === 1000 ? "1L" : `${item.size_ml}ml`;
                    const packLabel = item.packaging_type === "pouch" ? "Foil pouch" : "Glass jar";
                    return (
                      <motion.div
                        key={item.signature}
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        data-testid={`cart-item-${item.signature}`}
                        className="bg-white border border-forest/12 rounded-xl p-3"
                      >
                        <div className="flex gap-3">
                          <CartItemThumb item={item} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-sans font-semibold text-sm sm:text-[15px] text-forest leading-snug m-0">
                                  {item.name}
                                </p>
                                <p className="text-xs text-botanical mt-0.5 m-0 leading-snug">
                                  {sizeLabel} · {packLabel}
                                </p>
                                <p className="text-xs text-botanical/80 mt-0.5 m-0 tabular-nums">
                                  £{unit.toFixed(2)} each
                                </p>
                              </div>
                              <p className="font-sans font-semibold text-sm sm:text-[15px] text-forest tabular-nums m-0 shrink-0">
                                £{lineTotal.toFixed(2)}
                              </p>
                            </div>

                            <div className="mt-2.5 flex items-center justify-between gap-2">
                              <div className="inline-flex items-center rounded-full border-2 border-forest/25 bg-cream/60">
                                <button
                                  type="button"
                                  className="w-9 h-9 min-w-[36px] grid place-items-center text-forest hover:bg-forest hover:text-cream rounded-l-full transition"
                                  onClick={() => updateQty(item.signature, item.qty - 1)}
                                  data-testid={`qty-decrease-${item.signature}`}
                                  aria-label="Decrease quantity"
                                >
                                  <Minus className="w-4 h-4" strokeWidth={2.25} />
                                </button>
                                <span
                                  className="min-w-[2rem] px-1 text-center text-sm font-semibold text-forest tabular-nums"
                                  data-testid={`qty-${item.signature}`}
                                >
                                  {item.qty}
                                </span>
                                <button
                                  type="button"
                                  className="w-9 h-9 min-w-[36px] grid place-items-center text-forest hover:bg-forest hover:text-cream rounded-r-full transition"
                                  onClick={() => updateQty(item.signature, item.qty + 1)}
                                  data-testid={`qty-increase-${item.signature}`}
                                  aria-label="Increase quantity"
                                >
                                  <Plus className="w-4 h-4" strokeWidth={2.25} />
                                </button>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeItem(item.signature)}
                                data-testid={`remove-item-${item.signature}`}
                                className="text-xs font-medium text-forest/70 hover:text-forest underline underline-offset-2"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}

                  <div className="pt-1">
                    <p className="text-[11px] tracking-[0.16em] uppercase text-botanical/70 font-medium m-0 mb-2">
                      Delivery
                    </p>
                    <div className="grid gap-2">
                      {DELIVERY_OPTIONS.map((opt) => {
                        const selected = deliveryOption === opt.id;
                        return (
                          <label
                            key={opt.id}
                            className={`flex items-start gap-3 cursor-pointer rounded-xl border px-3.5 py-3 transition ${
                              selected ? "border-gold bg-white shadow-sm" : "border-cream bg-white/90"
                            }`}
                          >
                            <input
                              type="radio"
                              name="delivery-option"
                              checked={selected}
                              onChange={() => setDeliveryOption(opt.id)}
                              className="mt-1 accent-[#C9A449]"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="flex items-baseline justify-between gap-2">
                                <span className="font-medium text-sm text-forest leading-snug">{opt.label}</span>
                                <span className="font-semibold text-sm text-forest tabular-nums shrink-0">
                                  £{opt.priceGbp.toFixed(2)}
                                </span>
                              </span>
                              <span className="block text-xs text-botanical/70 mt-0.5 leading-snug">
                                {DELIVERY_CARD_COPY[opt.id] || opt.copy}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="rounded-xl border border-cream bg-cream/50 px-3.5 py-3">
                    <p className="text-sm font-medium text-forest m-0">Freshly prepared in the UK</p>
                    <p className="text-xs text-botanical/75 mt-1 m-0 leading-snug">
                      UK orders only. Usually prepared within 1–3 working days.
                    </p>
                  </div>

                  <CommunityRewardCartNote />
                </div>

                <div className="border-t border-cream px-5 sm:px-6 py-4 bg-white space-y-3 shrink-0">
                  <div className="flex items-center justify-between text-sm text-botanical">
                    <span>Subtotal</span>
                    <span data-testid="cart-subtotal" className="tabular-nums">
                      £{subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-botanical">
                    <span>Delivery</span>
                    <span className="tabular-nums">£{shipping.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-cream">
                    <span className="text-sm font-medium text-forest">Total</span>
                    <span className="font-sans font-semibold text-xl text-forest tabular-nums" data-testid="cart-total">
                      £{total.toFixed(2)}
                    </span>
                  </div>
                  {checkoutError ? (
                    <p className="text-xs text-red-800 bg-red-50 border border-red-100 rounded-xl px-3 py-2 leading-relaxed m-0">
                      {checkoutError}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    onClick={checkout}
                    disabled={checkoutLoading}
                    data-testid="checkout-btn"
                    className="w-full min-h-[52px] inline-flex items-center justify-center rounded-full bg-gold text-forest py-3.5 text-sm font-semibold hover:bg-goldhover transition disabled:opacity-60 disabled:pointer-events-none"
                  >
                    {checkoutLoading ? "Redirecting…" : "Proceed to Checkout"}
                  </button>
                  <p className="text-[11px] text-botanical/60 text-center m-0 leading-snug">
                    Secure checkout powered by Stripe.
                  </p>
                </div>
              </>
            )}
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
};

export default CartDrawer;
